import React, { useState } from 'react';
import { ChevronDown, ArrowRight, ArrowLeft } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../hooks/useLanguage';
type CategoryKey = 'shareholders' | 'boardOfDirectors' | 'executiveCommittee';
interface Person {
  id: string;
  nameKey: string;
  image: string;
  descriptionKey: string;
  category: CategoryKey;
}
const people: Person[] = [{
  id: 'john-smith',
  nameKey: 'johnSmith',
  image: 'https://cdn.magicpatterns.com/uploads/jUhmsjpJ7X6smQdUcGbpXD/Mask_Group_170.jpg',
  descriptionKey: 'roleDescription',
  category: 'shareholders'
}, {
  id: 'jessica-doe',
  nameKey: 'jessicaDoe',
  image: 'https://cdn.magicpatterns.com/uploads/hsLEuikCCbQ1DNnvm6ALm8/Mask_Group_172.jpg',
  descriptionKey: 'roleDescription',
  category: 'shareholders'
}, {
  id: 'rea-johnson',
  nameKey: 'reaJohnson',
  image: 'https://cdn.magicpatterns.com/uploads/rdgLQvk2TTEc4F7swVS2va/Mask_Group_171.jpg',
  descriptionKey: 'roleDescription',
  category: 'shareholders'
}, {
  id: 'john-smith-board',
  nameKey: 'johnSmith',
  image: 'https://cdn.magicpatterns.com/uploads/jUhmsjpJ7X6smQdUcGbpXD/Mask_Group_170.jpg',
  descriptionKey: 'roleDescription',
  category: 'boardOfDirectors'
}, {
  id: 'jessica-doe-board',
  nameKey: 'jessicaDoe',
  image: 'https://cdn.magicpatterns.com/uploads/hsLEuikCCbQ1DNnvm6ALm8/Mask_Group_172.jpg',
  descriptionKey: 'roleDescription',
  category: 'boardOfDirectors'
}, {
  id: 'rea-johnson-exec',
  nameKey: 'reaJohnson',
  image: 'https://cdn.magicpatterns.com/uploads/rdgLQvk2TTEc4F7swVS2va/Mask_Group_171.jpg',
  descriptionKey: 'roleDescription',
  category: 'executiveCommittee'
}];
const categoryKeys: CategoryKey[] = ['shareholders', 'boardOfDirectors', 'executiveCommittee'];
export function GovernanceGrid() {
  const [activeCategory, setActiveCategory] = useState<CategoryKey>('shareholders');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const {
    ref,
    isVisible
  } = useScrollReveal();
  const {
    t,
    direction
  } = useLanguage();
  const categoryLabels: Record<CategoryKey, string> = {
    shareholders: t('corporateGovernancePage.grid.shareholders'),
    boardOfDirectors: t('corporateGovernancePage.grid.boardOfDirectors'),
    executiveCommittee: t('corporateGovernancePage.grid.executiveCommittee')
  };
  const filteredPeople = people.filter(p => p.category === activeCategory);
  const ArrowIcon = direction === 'rtl' ? ArrowLeft : ArrowRight;
  return <section ref={ref} className="py-20 bg-white relative z-10" aria-label="Governance Team" data-id="element-202">
      <div className="container mx-auto px-6 md:px-12" data-id="element-203">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-204">
          {/* Desktop Filters - Left Sidebar */}
          <div className="hidden lg:block w-64 flex-shrink-0" data-id="element-205">
            <div className="sticky top-24 space-y-6" data-id="element-206">
              {categoryKeys.map((key, index) => <button key={key} onClick={() => setActiveCategory(key)} className={`w-full flex items-center justify-between group transition-all duration-300 ${direction === 'rtl' ? 'text-right' : 'text-left'} ${activeCategory === key ? 'text-[#061D76] font-bold' : 'text-[#25B8D6] hover:text-[#061D76]'}`} data-id="element-207">
                  <div className="flex items-center gap-4" data-id="element-208">
                    <span className={`text-sm font-medium ${activeCategory === key ? 'text-[#061D76]' : 'text-[#25B8D6]'}`} data-id="element-209">
                      {index + 1}
                    </span>
                    <span className="text-lg" data-id="element-210">{categoryLabels[key]}</span>
                  </div>

                  {/* Arrow Icon - Visible only for active item */}
                  <span className={`transition-opacity duration-300 ${activeCategory === key ? 'opacity-100' : 'opacity-0'}`} data-id="element-211">
                    <ArrowIcon className="w-5 h-5 text-[#061D76]" data-id="element-212" />
                  </span>
                </button>)}
            </div>
          </div>

          {/* Mobile Filter - Dropdown */}
          <div className="lg:hidden relative z-20" data-id="element-213">
            <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="w-full bg-white border border-gray-200 rounded-lg px-6 py-4 flex items-center justify-between text-[#061D76] font-bold text-lg shadow-sm" data-id="element-214">
              <span data-id="element-215">{categoryLabels[activeCategory]}</span>
              <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-216" />
            </button>

            {isDropdownOpen && <div className="absolute top-full left-0 right-0 w-full mt-2 bg-white border border-gray-100 rounded-lg shadow-xl overflow-hidden z-30" data-id="element-217">
                {categoryKeys.map(key => <button key={key} onClick={() => {
              setActiveCategory(key);
              setIsDropdownOpen(false);
            }} className={`w-full px-6 py-3 hover:bg-gray-50 transition-colors ${direction === 'rtl' ? 'text-right' : 'text-left'} ${activeCategory === key ? 'text-[#25B8D6] font-bold' : 'text-[#061D76]'}`} data-id="element-218">
                    {categoryLabels[key]}
                  </button>)}
              </div>}
          </div>

          {/* People Grid */}
          <div className="flex-1 min-h-[600px]" data-id="element-219">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-220">
              {filteredPeople.map((person, index) => <div key={person.id} className={`relative aspect-[3/4] overflow-hidden group reveal-hidden ${isVisible ? 'animate-fade-up' : ''}`} style={{
              animationDelay: `${index * 100}ms`
            }} data-id="element-221">
                  {/* Image */}
                  <img src={person.image} alt={t(`corporateGovernancePage.grid.${person.nameKey}`)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-222" />

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#061D76]/90 via-[#061D76]/40 to-transparent opacity-90" data-id="element-223" />

                  {/* Text Content */}
                  <div className="absolute bottom-0 left-0 w-full p-6 text-white text-center" data-id="element-224">
                    <h3 className="text-2xl font-bold mb-2" data-id="element-225">
                      {t(`corporateGovernancePage.grid.${person.nameKey}`)}
                    </h3>
                    <p className="text-sm leading-relaxed text-white/90" data-id="element-226">
                      {t(`corporateGovernancePage.grid.${person.descriptionKey}`)}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Empty State */}
            {filteredPeople.length === 0 && <div className="py-20 text-center text-gray-400" data-id="element-227">
                <p data-id="element-228">No members found in this category.</p>
              </div>}
          </div>
        </div>
      </div>
    </section>;
}